/**
 * The CRM profile fields — the ONE definition of them, rendered by both the
 * create form and the edit form.
 *
 * WHY A SHARED COMPONENT AND NOT TWO COPIES. Exactly the reasoning behind
 * `useQuickAdd` (CLAUDE.md §12): the natural way to add these fields to a
 * second form is to copy the first one, and three months later the two build
 * different payloads and only one of them sends the referrer. So the value
 * object, the payload builder and the markup live here, once, and both forms
 * render this.
 *
 * WHY NOTHING HERE NAMES A VALUE. Every picker is fed from a lookup
 * vocabulary — «سرنخ واجد شرایط», «کلینیک» and «تهران» are seed rows, not
 * strings in this file (CLAUDE.md §1). An ADMIN who needs one that is missing
 * adds it inline, because `VocabularyField` opens its own creator; a USER gets
 * a sentence naming what to ask for, because creating a vocabulary row is
 * ADMIN at the BFF and again at the service.
 *
 * THE ONE RULE WITH TEETH: «نام مرکز» is required when the chosen «نوع مخاطب»
 * carries `requiresDetail`. That flag is a property of the vocabulary ROW, not
 * a code compared in this file — so a deployment with its own facility types
 * ticks the box on whichever of theirs needs a name and changes nothing here.
 * The server enforces it too; this is the half that tells you before you save.
 */
import { useMutation, useQuery } from '@apollo/client';
import { Button } from '@meshhub/design-tokens/ui';
import { Search, X } from 'lucide-react';
import React, { useState } from 'react';
import {
  ADMIN_LOOKUPS_QUERY,
  CONTACTS_QUERY,
  CREATE_LOOKUP,
} from '../../lib/queries.js';
import { S } from '../../lib/strings.js';
import { useSession } from '../../lib/session.jsx';
import { CheckboxField, SelectField, TextField } from '../FormKit.web.jsx';
import { VocabularyField } from '../VocabularyField.web.jsx';

/** The blank value object. Exported so both forms start from the same shape. */
export const EMPTY_CRM_FIELDS = {
  cityId: '',
  professionalCode: '',
  lifecycleStageId: '',
  salesStageId: '',
  customerHealthId: '',
  engagementStatusId: '',
  isGold: false,
  facilityTypeId: '',
  centerName: '',
  acquisitionSourceId: '',
  referrerContactId: '',
};

/** The same values read off an existing contact, for the edit form. */
export function crmFieldsOf(contact) {
  return {
    cityId: contact.city?.id ?? '',
    professionalCode: contact.professionalCode ?? '',
    lifecycleStageId: contact.lifecycleStage?.id ?? '',
    salesStageId: contact.salesStage?.id ?? '',
    customerHealthId: contact.customerHealth?.id ?? '',
    engagementStatusId: contact.engagementStatus?.id ?? '',
    isGold: Boolean(contact.isGold),
    facilityTypeId: contact.facilityType?.id ?? '',
    centerName: contact.centerName ?? '',
    acquisitionSourceId: contact.acquisitionSource?.id ?? '',
    referrerContactId: contact.referrerContactId ?? '',
  };
}

/**
 * The GraphQL input slice. Both `CreateContactInput` and `UpdateContactInput`
 * accept exactly these keys, so one builder serves both — which is what makes
 * "created with a referrer" and "edited to have a referrer" impossible to
 * disagree.
 *
 * An empty picker becomes `null`, not `''`: on update, null is what clears a
 * field, and the BFF's mask sends whichever key it is given.
 */
export function crmFieldsInput(values) {
  return {
    cityId: values.cityId || null,
    professionalCode: values.professionalCode.trim() || null,
    lifecycleStageId: values.lifecycleStageId || null,
    salesStageId: values.salesStageId || null,
    customerHealthId: values.customerHealthId || null,
    engagementStatusId: values.engagementStatusId || null,
    isGold: Boolean(values.isGold),
    facilityTypeId: values.facilityTypeId || null,
    // NOT NULL in the schema: the server stores '' for "unrecorded".
    centerName: values.centerName.trim(),
    acquisitionSourceId: values.acquisitionSourceId || null,
    referrerContactId: values.referrerContactId || null,
  };
}

/**
 * The client-side half of the center-name rule. Returns a Persian message, or
 * null when the pair is fine.
 *
 * Reads `requiresDetail` off the chosen row rather than comparing its code, so
 * this function knows nothing about clinics or hospitals.
 */
export function centerNameError(values, facilityTypes) {
  const chosen = facilityTypes.find((f) => f.id === values.facilityTypeId);
  if (!chosen?.requiresDetail) return null;
  return values.centerName.trim() ? null : S.contacts.detail.centerNameRequired;
}

/** One `lookups` query per vocabulary, plus the inline creator's mutation. */
function useVocabulary(kind) {
  const { isAdmin } = useSession();
  const variables = { kind, includeInactive: false };
  const { data, loading } = useQuery(ADMIN_LOOKUPS_QUERY, { variables });
  const [createLookup, { loading: creating, error }] = useMutation(CREATE_LOOKUP, {
    refetchQueries: [{ query: ADMIN_LOOKUPS_QUERY, variables }],
    // The new row must EXIST by the time the value points at it, so the
    // mutation resolves after its refetch (the VocabularyField contract).
    awaitRefetchQueries: true,
  });
  return {
    options: data?.lookups ?? [],
    loading,
    canCreate: isAdmin,
    creating,
    createError: error,
    onCreate: async ({ labelFa, code }) => {
      const { data: created } = await createLookup({
        variables: { input: { kind, code, labelFa } },
      });
      return created?.createLookup?.id;
    },
  };
}

/**
 * A vocabulary picker wired to its kind.
 *
 * EXPORTED, because quick-add needs exactly this for «حوزه فعالیت» and «محصول
 * مورد علاقه». Those two were plain `<select>`s fed from the same
 * `lookups(kind:)` query, which meant an empty vocabulary rendered as a
 * dropdown containing nothing and saying nothing — indistinguishable from a
 * list that failed to load — and there was no way to add an option from the
 * form where somebody had just discovered it was missing.
 */
export function LookupField({ kind, label, value, onChange, hint }) {
  const vocabulary = useVocabulary(kind);
  return (
    <VocabularyField
      label={label}
      hint={hint}
      value={value}
      onChange={onChange}
      includeNone
      codePrefix={kind}
      {...vocabulary}
    />
  );
}

/**
 * Pick a contact as the referrer.
 *
 * Deliberately the same shape as the affiliation dialog's picker — a search
 * box narrowing a select — because it is the same question, and a second way
 * of choosing a contact would be a second thing to learn. Unlike affiliations
 * it accepts either kind: a clinic can introduce a doctor and a doctor can
 * introduce a clinic.
 */
function ReferrerField({ value, currentName, excludeId, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // The name of whoever was picked in THIS session. Apollo returns no data for
  // a skipped query, so once the picker closes the candidate list is gone and
  // the closed state would fall back to the name the server sent — which is
  // the PREVIOUS referrer's, until a save and a refetch. Remembering it here
  // is what stops the field showing the person you just replaced.
  const [pickedName, setPickedName] = useState(null);

  const { data, loading } = useQuery(CONTACTS_QUERY, {
    variables: { page: 1, pageSize: 25, search: search.trim() || null },
    skip: !open,
  });

  const candidates = (data?.contacts?.items ?? []).filter((c) => c.id !== excludeId);
  // The chosen referrer's own name, so an already-linked contact reads as a
  // person rather than as a UUID that happens to be selected.
  const chosen = candidates.find((c) => c.id === value);
  const chosenName = chosen?.displayName ?? pickedName ?? currentName ?? '';

  if (!open) {
    return (
      <div className="flex items-end gap-2">
        <div className="flex flex-1 flex-col gap-1">
          <span className="text-sm text-text-muted">{S.contacts.detail.referrer}</span>
          <span className="text-base">
            {value ? chosenName || S.app.none : S.contacts.detail.noReferrer}
          </span>
        </div>
        <Button type="button" variant="ghost" onClick={() => setOpen(true)}>
          <Search size={14} aria-hidden="true" />
          {value ? S.contacts.detail.changeReferrer : S.contacts.detail.addReferrer}
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            title={S.contacts.detail.removeReferrer}
            aria-label={S.contacts.detail.removeReferrer}
            onClick={() => {
              setPickedName(null);
              onChange('');
            }}
          >
            <X size={14} aria-hidden="true" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 rounded border border-border bg-surface p-3">
      <TextField
        label={S.contacts.detail.referrer}
        placeholder={S.contacts.detail.searchContact}
        value={search}
        autoFocus
        onChange={(event) => setSearch(event.target.value)}
        // Enter here means "narrow the list", never "save the contact" — the
        // surrounding FormDialog is a <form>, and without this the key that
        // most obviously finishes this input would submit a half-filled form.
        onKeyDown={(event) => event.key === 'Enter' && event.preventDefault()}
      />
      <SelectField
        label={S.app.search}
        value={value}
        onChange={(event) => {
          const id = event.target.value;
          setPickedName(candidates.find((c) => c.id === id)?.displayName ?? null);
          onChange(id);
          setOpen(false);
        }}
      >
        <option value="">{loading ? S.app.loading : S.app.none}</option>
        {candidates.map((candidate) => (
          <option key={candidate.id} value={candidate.id}>
            {candidate.displayName}
          </option>
        ))}
      </SelectField>
    </div>
  );
}

/**
 * @param values         the object from EMPTY_CRM_FIELDS / crmFieldsOf
 * @param onChange       called with a patch
 * @param contactId      the contact being edited, excluded from the referrer list
 * @param referrerName   the referrer's name as the server resolved it
 * @param facilityTypes  the FACILITY_TYPE rows, hoisted so the caller can
 *                       validate `centerNameError` before submitting
 */
export function ContactCrmFields({
  values,
  onChange,
  contactId,
  referrerName,
  facilityTypes = [],
}) {
  const centerError = centerNameError(values, facilityTypes);
  const centerRequired = Boolean(
    facilityTypes.find((f) => f.id === values.facilityTypeId)?.requiresDetail,
  );

  return (
    <>
      <Section title={S.contacts.detail.sections.professional}>
        <LookupField
          kind="CITY"
          label={S.contacts.detail.city}
          value={values.cityId}
          onChange={(id) => onChange({ cityId: id })}
        />
        <TextField
          label={S.contacts.detail.professionalCode}
          value={values.professionalCode}
          dir="ltr"
          onChange={(event) => onChange({ professionalCode: event.target.value })}
        />
      </Section>

      <Section title={S.contacts.detail.sections.crm}>
        <LookupField
          kind="LIFECYCLE_STAGE"
          label={S.contacts.detail.lifecycleStage}
          value={values.lifecycleStageId}
          onChange={(id) => onChange({ lifecycleStageId: id })}
        />
        <LookupField
          kind="SALES_STAGE"
          label={S.contacts.detail.salesStage}
          value={values.salesStageId}
          onChange={(id) => onChange({ salesStageId: id })}
        />
        <LookupField
          kind="CUSTOMER_HEALTH"
          label={S.contacts.detail.customerHealth}
          value={values.customerHealthId}
          onChange={(id) => onChange({ customerHealthId: id })}
        />
        <LookupField
          kind="ENGAGEMENT_STATUS"
          label={S.contacts.detail.engagementStatus}
          value={values.engagementStatusId}
          onChange={(id) => onChange({ engagementStatusId: id })}
        />
        <div className="col-span-2 flex items-end">
          <CheckboxField
            label={S.contacts.detail.isGold}
            checked={values.isGold}
            onChange={(event) => onChange({ isGold: event.target.checked })}
          />
        </div>
      </Section>

      <Section title={S.contacts.detail.sections.facility}>
        <LookupField
          kind="FACILITY_TYPE"
          label={S.contacts.detail.facilityType}
          value={values.facilityTypeId}
          onChange={(id) => onChange({ facilityTypeId: id })}
        />
        <TextField
          label={S.contacts.detail.centerName}
          value={values.centerName}
          error={centerError}
          // The asterisk follows the chosen ROW's flag, not the current error,
          // so it appears the moment «کلینیک» is picked rather than after the
          // field has already been left empty. Same source the server reads.
          required={centerRequired || undefined}
          onChange={(event) => onChange({ centerName: event.target.value })}
        />
      </Section>

      <Section title={S.contacts.detail.sections.relations}>
        <LookupField
          kind="ACQUISITION_SOURCE"
          label={S.contacts.detail.acquisitionSource}
          value={values.acquisitionSourceId}
          onChange={(id) => onChange({ acquisitionSourceId: id })}
        />
        <div className="col-span-2">
          <ReferrerField
            value={values.referrerContactId}
            currentName={referrerName}
            excludeId={contactId}
            onChange={(id) => onChange({ referrerContactId: id })}
          />
        </div>
      </Section>
    </>
  );
}

function Section({ title, children }) {
  return (
    <section className="border-t border-border pt-4">
      <h3 className="mb-3 text-sm font-medium text-text-muted">{title}</h3>
      <div className="grid grid-cols-2 gap-4">{children}</div>
    </section>
  );
}
